import { useParentSize } from "@visx/responsive";
import { clsx } from "clsx";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { OptionRawSelect, RawSelect } from "../Form/RawSelect";
import { YearMonthSelect } from "../Form/YearMonthSelect";
import { DateRange } from "../../../features/Charts/hooks/useDateRangeFilter";

export function ChartContainer({
    title,
    children,
    description,
    isCondensed = true,
    isSnapshot = false,
    titleLink,
    periodFilter,
    dateRangeFilter,
}: {
    title: string;
    description: string;
    children: React.FC<{
        width: number;
        height: number;
    }>;
    isCondensed?: boolean;
    isSnapshot?: boolean;
    titleLink?: {
        label: string;
        href?: string;
        onClick?: () => void;
    };
    periodFilter?: {
        selectedPeriod: OptionRawSelect;
        onPeriodChange: (value: OptionRawSelect | OptionRawSelect[]) => void;
        options?: OptionRawSelect[];
    };
    dateRangeFilter?: {
        dateRange: DateRange;
        onDateRangeChange: (range: DateRange) => void;
        availableDates: string[];
    };
}) {
    const {
        parentRef: containerRef,
        width: containerWidth,
        height: containerHeight,
    } = useParentSize({ debounceTime: 15 });
    const { parentRef: headerRef, height: headerHeight } = useParentSize({
        ignoreDimensions: ["width"],
    });
    const containerPadding = 32;
    const width = containerWidth - 16;
    const height = containerHeight - headerHeight - containerPadding;

    const defaultPeriodOptions: OptionRawSelect[] = [
        { label: "Monthly", value: "monthly" },
        { label: "Quarterly", value: "quarterly" },
        { label: "Yearly", value: "yearly" },
    ];

    return (
        <div
            className={clsx(
                "border-default flex flex-col rounded border",
                isCondensed ? "h-[680px]" : "h-[760px]",
            )}
        >
            <div
                className={clsx(
                    "bg-secondary",
                    "flex h-full flex-col text-clip rounded p-6",
                )}
                ref={containerRef}
            >
                <div className="header mb-4" ref={headerRef}>
                    <div className={clsx("", "items-start justify-between")}>
                        <div
                            className={clsx(
                                "mb-4",
                                titleLink
                                    ? "flex w-full items-baseline justify-between gap-2"
                                    : "",
                            )}
                        >
                            <h1 className="heading-m">{title}</h1>
                            {titleLink?.href && (
                                <a
                                    onClick={titleLink.onClick}
                                    href={titleLink.href}
                                    className={clsx(
                                        "group flex items-center gap-1",
                                    )}
                                >
                                    {titleLink.label}
                                    <ArrowTopRightOnSquareIcon
                                        width={16}
                                        height={16}
                                    />
                                </a>
                            )}

                            {titleLink?.onClick && (
                                <button
                                    onClick={titleLink.onClick}
                                    className={clsx(
                                        "flex items-center gap-1",
                                        "faux-link",
                                    )}
                                >
                                    {titleLink.label}
                                    <ArrowTopRightOnSquareIcon
                                        width={16}
                                        height={16}
                                    />
                                </button>
                            )}
                        </div>

                        {!isSnapshot && (
                            <p className="body-s subdued">{description}</p>
                        )}

                        {(periodFilter || dateRangeFilter) && (
                            <div className="mt-4 space-y-4">
                                {periodFilter && (
                                    <div className="flex items-center justify-end">
                                        <div className="w-48">
                                            <RawSelect
                                                name="period-filter"
                                                label=""
                                                options={
                                                    periodFilter.options ||
                                                    defaultPeriodOptions
                                                }
                                                selected={
                                                    periodFilter.selectedPeriod
                                                }
                                                onChange={
                                                    periodFilter.onPeriodChange
                                                }
                                                testId="chart-period-filter"
                                            />
                                        </div>
                                    </div>
                                )}

                                {dateRangeFilter && (
                                    <div
                                        className={clsx(
                                            "w-full",
                                            "flex items-center justify-end gap-2",
                                        )}
                                    >
                                        <div
                                            className={clsx(
                                                "flex gap-2",
                                                isCondensed
                                                    ? "w-full"
                                                    : "w-1/3",
                                            )}
                                        >
                                            <div className="flex-1">
                                                <YearMonthSelect
                                                    value={
                                                        dateRangeFilter
                                                            .dateRange.startDate
                                                    }
                                                    onChange={(value) =>
                                                        dateRangeFilter.onDateRangeChange(
                                                            {
                                                                ...dateRangeFilter.dateRange,
                                                                startDate:
                                                                    value,
                                                            },
                                                        )
                                                    }
                                                    label=""
                                                    availableDates={
                                                        dateRangeFilter.availableDates
                                                    }
                                                    placeholder="Start date"
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <YearMonthSelect
                                                    value={
                                                        dateRangeFilter
                                                            .dateRange.endDate
                                                    }
                                                    onChange={(value) =>
                                                        dateRangeFilter.onDateRangeChange(
                                                            {
                                                                ...dateRangeFilter.dateRange,
                                                                endDate: value,
                                                            },
                                                        )
                                                    }
                                                    label=""
                                                    availableDates={
                                                        dateRangeFilter.availableDates
                                                    }
                                                    placeholder="End date"
                                                />
                                            </div>
                                        </div>
                                        <div className="w-20">
                                            <button
                                                onClick={() =>
                                                    dateRangeFilter.onDateRangeChange(
                                                        {
                                                            startDate: "",
                                                            endDate: "",
                                                        },
                                                    )
                                                }
                                                className="secondary w-full"
                                            >
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="h-full pt-0">{children({ width, height })}</div>
            </div>
        </div>
    );
}
